import { MapPin, Star, Clock, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { MarketplaceProvider } from '../types';
import VerifiedBadge from './VerifiedBadge';

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  DOCTOR: { bg: 'bg-blue-50', text: 'text-blue-700' },
  LAB: { bg: 'bg-purple-50', text: 'text-purple-700' },
  HOSPITAL: { bg: 'bg-red-50', text: 'text-red-700' },
  PHARMACY: { bg: 'bg-green-50', text: 'text-green-700' },
  AMBULANCE: { bg: 'bg-orange-50', text: 'text-orange-700' },
};

const TYPE_ICONS: Record<string, string> = {
  DOCTOR: '🩺',
  LAB: '🔬',
  HOSPITAL: '🏥',
  PHARMACY: '💊',
  AMBULANCE: '🚑',
};

interface ProviderCardProps {
  provider: MarketplaceProvider;
  compact?: boolean;
}

export default function ProviderCard({ provider, compact = false }: ProviderCardProps) {
  const navigate = useNavigate();
  const colors = TYPE_COLORS[provider.type] || TYPE_COLORS.DOCTOR;

  return (
    <div
      onClick={() => navigate(`/patient/marketplace/${provider.type.toLowerCase()}/${provider.slug}`)}
      className="card p-4 hover:shadow-md transition-shadow cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center text-xl shrink-0`}>
          {provider.logoUrl ? (
            <img src={provider.logoUrl} alt={provider.name} className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            TYPE_ICONS[provider.type]
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-gray-900 group-hover:text-teal-700 transition-colors truncate">
              {provider.name}
            </h3>
            {provider.isVerified && <VerifiedBadge size="sm" />}
          </div>

          {provider.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {provider.specialties.slice(0, compact ? 2 : 4).map(s => (
                <span key={s} className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                  {s}
                </span>
              ))}
              {provider.specialties.length > (compact ? 2 : 4) && (
                <span className="text-[10px] text-gray-400">+{provider.specialties.length - (compact ? 2 : 4)}</span>
              )}
            </div>
          )}

          {!compact && provider.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{provider.description}</p>
          )}

          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            {provider.rating > 0 && (
              <span className="flex items-center gap-0.5">
                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                <span className="font-medium text-gray-700">{provider.rating.toFixed(1)}</span>
                <span className="text-gray-400">({provider.reviewCount})</span>
              </span>
            )}
            {provider.city && (
              <span className="flex items-center gap-0.5">
                <MapPin className="w-3.5 h-3.5" /> {provider.city}
              </span>
            )}
            {provider.openHours && (
              <span className="flex items-center gap-0.5">
                <Clock className="w-3.5 h-3.5" /> {provider.openHours}
              </span>
            )}
            {!compact && provider.phone && (
              <span className="flex items-center gap-0.5">
                <Phone className="w-3.5 h-3.5" /> {provider.phone}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
